import { useState, useRef } from "react";
import { Camera, Upload, Loader2, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface ImageUploadProps {
  userId: string;
  currentImage?: string;
  onImageUploaded: (url: string) => void;
  className?: string;
}

export const ImageUpload = ({ userId, currentImage, onImageUploaded, className }: ImageUploadProps) => {
  const [preview, setPreview] = useState<string | null>(currentImage || null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): boolean => {
    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      toast({ title: "Formato non supportato. Usa JPG, PNG o WEBP.", variant: "error" });
      return false;
    }

    if (file.size > maxSize) {
      toast({ title: "File troppo grande. Massimo 5MB.", variant: "error" });
      return false;
    }

    return true;
  };

  const deleteOldImage = async (imageUrl: string) => {
    try {
      const urlParts = imageUrl.split('/');
      const pathIndex = urlParts.indexOf('profile-images');
      if (pathIndex !== -1) {
        const filePath = urlParts.slice(pathIndex + 1).join('/');
        await supabase.storage.from('profile-images').remove([filePath]);
      }
    } catch (error) {
      console.error("Error deleting old image:", error);
    }
  };

  const uploadImage = async (file: File) => {
    if (!validateFile(file)) return;

    setIsUploading(true);
    try {
      // Show immediate preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Delete old image if exists
      if (currentImage) {
        await deleteOldImage(currentImage);
      }

      // Upload new image
      const fileExt = file.name.split('.').pop();
      const fileName = `profile-${Date.now()}.${fileExt}`;
      const filePath = `${userId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('profile-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('profile-images')
        .getPublicUrl(filePath);

      onImageUploaded(publicUrl);
      toast({ title: "Foto caricata con successo!", variant: "success" });
    } catch (error) {
      console.error("Upload error:", error);
      toast({ title: "Errore durante il caricamento. Riprova.", variant: "error" });
      setPreview(currentImage || null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadImage(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      uploadImage(file);
    }
  };

  const handleRemove = async () => {
    if (currentImage) {
      await deleteOldImage(currentImage);
      setPreview(null);
      onImageUploaded("");
      toast({ title: "Foto rimossa", variant: "success" });
    }
  };

  return (
    <div className={cn("flex items-start gap-6", className)}>
      {/* Preview */}
      <div className="relative">
        <div className="w-32 h-32 border-[3px] border-dark bg-surface flex items-center justify-center overflow-hidden">
          {preview ? (
            <img 
              src={preview} 
              alt="Profile preview" 
              className="w-full h-full object-cover object-center"
            />
          ) : (
            <Camera className="w-12 h-12 text-muted-foreground" />
          )}
        </div>
        {preview && !isUploading && (
          <button
            onClick={handleRemove}
            className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 border-2 border-dark hover:bg-destructive/90 transition-colors"
            type="button"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Upload Area */}
      <div className="flex-1 space-y-3">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />
        
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            "border-[3px] border-dashed border-dark p-6 cursor-pointer transition-all bg-surface hover:bg-accent/10",
            isDragging && "border-primary bg-primary/5",
            isUploading && "opacity-50 pointer-events-none"
          )}
        >
          <div className="flex flex-col items-center gap-2 text-center">
            {isUploading ? (
              <>
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
                <p className="text-sm font-medium">Caricamento...</p>
              </>
            ) : (
              <>
                <Upload className="w-8 h-8 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">
                    Clicca o trascina qui la tua foto
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    JPG, PNG o WEBP • Max 5MB
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          L'immagine sarà automaticamente tagliata in formato quadrato dal centro.
        </p>
      </div>
    </div>
  );
};
